import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom, map, switchMap, take } from 'rxjs';

import { HOTEL_SLUG } from '@valk-nx/core/lib/core';

export const NEWSLETTER_SUBSCRIBE_SERVICE_URL = new InjectionToken<string>(
	'newsletter-subscribe-service-url',
);

export const NEWSLETTER_API_COOKIES = new InjectionToken<string[]>(
	'newsletter_api-cookies',
);

@Injectable({ providedIn: 'root' })
export class NewsletterService {
	constructor(
		private readonly http: HttpClient,
		@Optional()
		@Inject(NEWSLETTER_SUBSCRIBE_SERVICE_URL)
		private readonly subscribeUrl: string,
		@Inject(NEWSLETTER_API_COOKIES)
		private readonly apiCookies: { withCredentials: boolean },
		@Inject(HOTEL_SLUG)
		private readonly hotelSlug: string,
		private readonly recaptchaV3Service: ReCaptchaV3Service,
	) {}

	newsletterSubscribe(
		emailAddress: string,
		recaptchaV2Token?: string,
	): Promise<
		| {
				success: boolean;
				status?: number;
		  }
		| { code: string; status?: number }
	> {
		if (recaptchaV2Token) {
			return this.recaptchaV2Submit(emailAddress, recaptchaV2Token);
		}
		return this.recaptchaV3Submit(emailAddress);
	}

	recaptchaV3Submit(emailAddress: string): Promise<{
		success: boolean;
		status?: number;
	}> {
		return firstValueFrom(
			this.recaptchaV3Service.execute('newsletterSubscribe').pipe(
				switchMap((token) => {
					return this.http.post<{ data: Record<string, never> }>(
						this.subscribeUrl,
						{
							emailAddress,
							hotelSlug: this.hotelSlug,
							token,
							recaptchaVersion: 'v3',
						},
						{
							...this.apiCookies,
							observe: 'body',
							responseType: 'json',
						},
					);
				}),
				take(1),
				map((body) => {
					return {
						success: body.data['success'],
					};
				}),
			),
		);
	}

	recaptchaV2Submit(
		emailAddress: string,
		recaptchaToken: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		return firstValueFrom(
			this.http
				.post<{ data: Record<string, never> }>(
					this.subscribeUrl,
					{
						emailAddress,
						hotelSlug: this.hotelSlug,
						token: recaptchaToken,
						recaptchaVersion: 'v2',
					},
					{
						...this.apiCookies,
						observe: 'body',
						responseType: 'json',
					},
				)
				.pipe(
					take(1),
					map((body) => {
						return {
							success: body.data['success'],
						};
					}),
				),
		);
	}
}
